// zohoOrg.js — the ACTIVE Zoho organization (id + sandbox flag), fetched from
// the backend.
//
// Staff-UI Zoho deep links must be built from this, never a hardcoded org id
// or base URL: on a _test database the backend targets the SANDBOX org, which
// lives on a DIFFERENT domain and path —
//   live:    https://books.zoho.eu/app/<orgId>#/...
//   sandbox: https://books.sandbox.zoho.eu/sandbox/<orgId>#/...
// (2026-08-09). On production the backend resolves the live org, so links
// are byte-identical to the old hardcoded ones — nothing changes for staff on
// the live system. One fetch per page load, shared module-level cache.

import { useEffect, useState } from 'react';
import axios from 'axios';

let cachedOrg = null; // { orgId, isSandbox }
let inflight = null;

export function getZohoOrg() {
    if (cachedOrg) return Promise.resolve(cachedOrg);
    if (!inflight) {
        inflight = axios
            .get('/api/clients/zoho-org')
            .then((res) => {
                const orgId = res.data?.org_id || null;
                cachedOrg = orgId ? { orgId, isSandbox: !!res.data?.is_sandbox } : null;
                return cachedOrg;
            })
            .catch((err) => {
                inflight = null; // allow a retry on the next call
                throw err;
            });
    }
    return inflight;
}

/** Returns { orgId, isSandbox } or null while loading — render no link until
 *  non-null (a wrong-org link is worse than a short delay). */
export function useZohoOrgId() {
    const [org, setOrg] = useState(cachedOrg);
    useEffect(() => {
        // The module cache may have filled between render and effect (another
        // component's fetch resolving) — adopt it, don't skip setState.
        if (cachedOrg) { setOrg(cachedOrg); return undefined; }
        let alive = true;
        getZohoOrg()
            .then((o) => { if (alive) setOrg(o); })
            .catch(() => { /* links simply don't render */ });
        return () => { alive = false; };
    }, []);
    return org;
}

const base = (org) => (org.isSandbox
    ? `https://books.sandbox.zoho.eu/sandbox/${org.orgId}`
    : `https://books.zoho.eu/app/${org.orgId}`);

export const zohoContactUrl = (org, contactId) => `${base(org)}#/contacts/${contactId}`;
export const zohoInvoiceUrl = (org, invoiceId) => `${base(org)}#/invoices/${invoiceId}`;
export const zohoPaymentUrl = (org, paymentId) => `${base(org)}#/paymentsreceived/${paymentId}`;
export const zohoItemUrl = (org, itemId) => `${base(org)}#/inventory/items/${itemId}`;
